import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Twitter, Instagram, ArrowUpRight, Send } from "lucide-react";
import { getSettings, openExternal } from "../lib/api";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LINKS = [
  { id: "x", label: "X / TWITTER", handle: "@MADEBYJRY", href: "https://x.com/madebyjry", icon: Twitter },
  { id: "instagram", label: "INSTAGRAM", handle: "@MADEBYJRY", href: "https://www.instagram.com/madebyjry/", icon: Instagram },
];

export default function ContactApp() {
  const [s, setS] = useState({ contact_kicker: "LET'S_WORK.BAT", contact_title: "READY TO ELEVATE YOUR CONTENT?" });
  const [form, setForm] = useState({ name: "", email: "", social: "", message: "" });
  const [status, setStatus] = useState("idle"); // idle | sending | sent | error

  useEffect(() => { getSettings().then(setS).catch(() => {}); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) return;
    setStatus("sending");
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("sent");
      setForm({ name: "", email: "", social: "", message: "" });
    } catch {
      setStatus("error");
    }
  };

  const inputClass = "w-full font-pixel text-[13px] px-3 py-2 retro-border outline-none focus:outline-none resize-none";
  const inputStyle = { background: "var(--cream)", color: "var(--black)", boxShadow: "3px 3px 0 rgba(0,0,0,0.4)" };

  return (
    <div className="h-full jry-scroll overflow-y-auto p-6 flex flex-col gap-5" style={{ background: "var(--red)" }} data-testid="contact-app">
      <div>
        <div className="font-pixel text-[20px] mb-1" style={{ color: "var(--black)" }}>&gt; {s.contact_kicker}</div>
        <h2 className="font-display text-4xl leading-[0.88]" style={{ color: "var(--black)" }}>
          {s.contact_title}
        </h2>
      </div>

      {/* Contact Form */}
      <form onSubmit={handleSubmit} className="flex flex-col gap-3">
        <div className="flex gap-3">
          <div className="flex-1">
            <div className="font-pixel text-[11px] mb-1" style={{ color: "var(--black)" }}>NAME</div>
            <input
              className={inputClass}
              style={inputStyle}
              placeholder="YOUR NAME"
              value={form.name}
              onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
              disabled={status === "sending" || status === "sent"}
            />
          </div>
          <div className="flex-1">
            <div className="font-pixel text-[11px] mb-1" style={{ color: "var(--black)" }}>EMAIL</div>
            <input
              type="email"
              className={inputClass}
              style={inputStyle}
              placeholder="YOUR@EMAIL.COM"
              value={form.email}
              onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
              disabled={status === "sending" || status === "sent"}
            />
          </div>
        </div>

        <div>
          <div className="font-pixel text-[11px] mb-1" style={{ color: "var(--black)" }}>SOCIAL HANDLE</div>
          <input
            className={inputClass}
            style={inputStyle}
            placeholder="@YOURHANDLE"
            value={form.social}
            onChange={e => setForm(f => ({ ...f, social: e.target.value }))}
            disabled={status === "sending" || status === "sent"}
          />
        </div>

        <div>
          <div className="font-pixel text-[11px] mb-1" style={{ color: "var(--black)" }}>MESSAGE</div>
          <textarea
            rows={4}
            className={inputClass}
            style={inputStyle}
            placeholder="TELL ME WHAT YOU NEED."
            value={form.message}
            onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
            disabled={status === "sending" || status === "sent"}
          />
        </div>

        {status === "sent" && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}
            className="font-pixel text-[13px] p-3 text-center retro-border"
            style={{ background: "var(--black)", color: "var(--cream)" }}>
            ✓ MESSAGE SENT — I'LL BE IN TOUCH.
          </motion.div>
        )}
        {status === "error" && (
          <div className="font-pixel text-[13px] p-2 text-center retro-border"
            style={{ background: "var(--cream)", color: "var(--red)" }}>
            ERROR — TRY AGAIN OR DM ME DIRECTLY.
          </div>
        )}

        {status !== "sent" && (
          <motion.button type="submit" whileHover={{ x: 3, y: -2 }}
            disabled={status === "sending" || !form.name || !form.email || !form.message}
            className="flex items-center justify-center gap-2 p-3 retro-border font-display text-[18px] disabled:opacity-50"
            style={{ background: "var(--black)", color: "var(--cream)", boxShadow: "5px 5px 0 rgba(0,0,0,0.5)" }}>
            <Send size={16} strokeWidth={2.5} />
            {status === "sending" ? "SENDING..." : "SEND MESSAGE"}
          </motion.button>
        )}
      </form>

      {/* Social Links */}
      <div className="space-y-3">
        {LINKS.map((l, i) => {
          const Icon = l.icon;
          return (
            <motion.button key={l.id} type="button" onClick={() => openExternal(l.href)}
              initial={{ opacity: 0, x: -16 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 + i * 0.08 }}
              whileHover={{ x: 5, y: -2 }}
              className="group w-full text-left flex items-center gap-3 p-3 retro-border"
              style={{ background: "var(--cream)", boxShadow: "5px 5px 0 rgba(0,0,0,0.5)" }}
              data-testid={`contact-${l.id}`}>
              <span className="w-11 h-11 flex items-center justify-center shrink-0" style={{ background: "var(--black)", color: "var(--cream)" }}>
                <Icon size={20} strokeWidth={2.5} />
              </span>
              <div className="flex-1">
                <div className="font-display text-[18px] leading-none" style={{ color: "var(--black)" }}>{l.label}</div>
                <div className="font-pixel text-[17px] leading-none" style={{ color: "var(--red-dark)" }}>{l.handle}</div>
              </div>
              <ArrowUpRight size={22} strokeWidth={3} style={{ color: "var(--black)" }} className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
